import axios from 'axios';
import api from './api';

// ─── Types (mirror apps/microsite serializers + sections registry) ─────────────

/** Page types = MicrositePageLayout.PAGE_TYPE_CHOICES. */
export type MicrositePageType = 'landing' | 'insurer' | 'product';

export const MICROSITE_PAGE_TYPES: { value: MicrositePageType; label: string }[] = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'insurer', label: 'Insurer Page' },
  { value: 'product', label: 'Product Page' },
];

/** Schema type tokens declared in apps/microsite/sections.py. */
export type SchemaToken = 'str' | 'text' | 'bool' | 'int' | 'url' | 'list' | 'dict';

/** One entry of the SECTION_TYPES registry (GET /microsite/section-types/). */
export interface SectionTypeDef {
  label: string;
  /** Flat `{field: token}` map, derived server-side from `fields`. */
  schema: Record<string, SchemaToken>;
  /** The authoritative structured schema (enums, nested objects, image pickers). */
  fields?: Record<string, unknown>;
}

/** The whole registry keyed by section `type`. */
export type SectionTypesRegistry = Record<string, SectionTypeDef>;

/**
 * The breakpoints a section can be shown or hidden on. Mirrors
 * `apps.microsite.sections.DEVICES` and `apps.microsite.devices.DEVICES`, which
 * classify the *request* server-side — an off-breakpoint section is never
 * rendered, so hiding a banner on mobile really does save the phone the download.
 */
export type MicrositeDevice = 'desktop' | 'tablet' | 'mobile';

export const MICROSITE_DEVICES: { value: MicrositeDevice; label: string; width: number }[] = [
  { value: 'desktop', label: 'Desktop', width: 1280 },
  { value: 'tablet', label: 'Tablet', width: 834 },
  { value: 'mobile', label: 'Mobile', width: 390 },
];

/** Per-breakpoint show/hide. A missing key means "shown". */
export type SectionVisibility = Partial<Record<MicrositeDevice, boolean>>;

/** A single section envelope stored on MicrositePageLayout.sections. */
export interface MicrositeSection {
  id: string;
  type: string;
  order?: number;
  config: Record<string, unknown>;
  /** Switched off everywhere without being deleted. */
  hidden?: boolean;
  /** Per-breakpoint overrides; absent = visible on all of them. */
  visibility?: SectionVisibility;
}

/**
 * A page layout row. Shape follows MicrositePageLayoutSerializer plus the extra
 * keys the ViewSet injects on list/retrieve (`is_customized`, `exists`).
 */
export interface MicrositeLayout {
  id?: string;
  broker?: string | null;
  broker_name?: string | null;
  page_type: MicrositePageType;
  sections: MicrositeSection[];
  is_published?: boolean;
  /** True when this is the shared country-admin BASE row (broker is NULL). */
  is_base?: boolean;
  /** True on a broker's own copy-on-customize row (list endpoint only). */
  is_customized?: boolean;
  /** False when nothing is seeded for this page type yet. */
  exists?: boolean;
  version?: number;
  created_at?: string;
  updated_at?: string;
}

export interface DomainSubmitResponse {
  domain: string;
  status: string;
  verification_token: string;
  instructions: string;
}

/**
 * The outcome of the TLS/nginx provisioning `DomainApproveView` triggers
 * automatically right after DNS verification succeeds (apps.microsite.
 * provisioning.ProvisionResult, subset). `applied` is only true once certbot,
 * the vhost, and both `nginx -t` checks all succeeded and nginx reloaded;
 * `ok=false` covers both "disabled" (MICROSITE_PROVISION_DOMAINS is False —
 * the state of every environment today) and a real provisioning failure. A
 * provisioning failure never undoes the DNS verification itself.
 */
export interface DomainProvisioningOutcome {
  ok: boolean;
  applied: boolean;
  detail: string;
}

export interface DomainApproveResponse {
  domain: string;
  status: string;
  broker: string;
  provisioning: DomainProvisioningOutcome;
}

/** Self-service public-site status (GET/PATCH /microsite/public-site/). */
export interface PublicSiteStatus {
  is_public_profile_enabled: boolean;
  /** True only when the public route will actually serve the site (#370). */
  is_live: boolean;
  broker_status: string;
  public_profile_slug: string | null;
  description: string;
  custom_domain: string;
  public_url: string | null;
  product_count: number;
}

export interface PublicSiteUpdate {
  is_public_profile_enabled?: boolean;
  public_profile_slug?: string;
  description?: string;
}

// ─── Products & Pages visibility (public show/hide) ─────────────────────────────

/** One sellable product under an insurer, with its public-page visibility flag. */
export interface MicrositeCatalogProduct {
  id: string;
  name: string;
  slug: string;
  visible: boolean;
}

/** An insurer, its public-page visibility flag, and its products. */
export interface MicrositeCatalogInsurer {
  id: string;
  name: string;
  slug: string;
  visible: boolean;
  products: MicrositeCatalogProduct[];
}

/**
 * The broker's public-site catalog with per-item visibility.
 *
 * Everything is public by default; hiding is an exception list, so `all_public`
 * is true whenever nothing is hidden and `hidden_count` is the size of the
 * hide-list (hidden insurers + hidden products).
 */
export interface MicrositeCatalog {
  all_public: boolean;
  insurer_count: number;
  hidden_count: number;
  insurers: MicrositeCatalogInsurer[];
}

/** Which kind of catalog item a visibility toggle targets. */
export type MicrositeVisibilityItemType = 'insurer' | 'product';

// ─── Theme ─────────────────────────────────────────────────────────────────────

/**
 * Theme tokens: `{group: {field: value}}`, e.g.
 * `{colors: {primary: '#1478c8'}, animations: {reveal: 'fade'}}`.
 *
 * The vocabulary is NOT hard-coded here — `GET /microsite/theme/` returns
 * `fields`, and the builder renders the form from that. One source of truth in
 * `apps/microsite/theme.py` means a new dial appears in the UI the moment the
 * backend offers it, and can never drift out of sync with what compiles to CSS.
 */
export type ThemeTokens = Record<string, Record<string, unknown>>;

/** A field spec: a bare type token, or `{type, choices}` for an enum. */
export type ThemeFieldSpec = string | { type: string; choices?: string[] };

export interface ThemeGroupDef {
  label: string;
  fields: Record<string, ThemeFieldSpec>;
}

/** Response of GET/PUT /microsite/theme/. */
export interface MicrositeThemeResponse {
  tokens: ThemeTokens;
  /** The server's own schema, keyed by group (colors, typography, …). */
  fields: Record<string, ThemeGroupDef>;
  /** What an unset theme behaves like — used to seed the form, not persisted. */
  defaults: ThemeTokens;
  is_base: boolean;
  /** True once the broker has its own row rather than inheriting the platform one. */
  is_customized: boolean;
  exists: boolean;
  version: number;
}

// ─── Reusable section templates ────────────────────────────────────────────────

/**
 * A configured section saved for reuse. Deliberately carries no section `id`:
 * ids must be unique within a page (the backend rejects duplicates), so one is
 * minted at insert time.
 */
export interface MicrositeSectionTemplate {
  id: string;
  name: string;
  section_type: string;
  config: Record<string, unknown>;
  /** True for a platform-provided template — visible to all, editable by none. */
  is_shared: boolean;
  created_at?: string;
  updated_at?: string;
}

// ─── Live preview ──────────────────────────────────────────────────────────────

export interface MicrositePreviewRequest {
  pageType: MicrositePageType;
  sections: MicrositeSection[];
  device: MicrositeDevice;
  /** Draft theme to preview; omit to use whatever is saved. */
  theme?: ThemeTokens | null;
}

export interface MicrositePreviewResponse {
  html: string;
  device: MicrositeDevice;
  page_type: MicrositePageType;
}

// ─── Service ───────────────────────────────────────────────────────────────────

const BASE = '/microsite';

export const micrositeService = {
  /** GET the effective layout for a page type within the caller's scope. */
  async getLayout(pageType: MicrositePageType): Promise<MicrositeLayout> {
    const res = await api.get<MicrositeLayout>(`${BASE}/layouts/${pageType}/`);
    return res.data;
  },

  /** PUT the ordered sections for a page type (creates the scoped row if absent). */
  async updateLayout(
    pageType: MicrositePageType,
    sections: MicrositeSection[],
    isPublished = true,
  ): Promise<MicrositeLayout> {
    const res = await api.put<MicrositeLayout>(`${BASE}/layouts/${pageType}/`, {
      sections,
      is_published: isPublished,
    });
    return res.data;
  },

  /** The section registry powering the builder palette + config forms. */
  async getSectionTypes(): Promise<SectionTypesRegistry> {
    const res = await api.get<SectionTypesRegistry>(`${BASE}/section-types/`);
    return res.data;
  },

  /** Copy the BASE sections into a fresh broker row (copy-on-customize). */
  async customize(pageType: MicrositePageType): Promise<MicrositeLayout> {
    const res = await api.post<MicrositeLayout>(`${BASE}/layouts/${pageType}/customize/`);
    return res.data;
  },

  /** Delete the broker's own row so the BASE layout is inherited again. */
  async resetToBase(pageType: MicrositePageType): Promise<void> {
    await api.post(`${BASE}/layouts/${pageType}/reset-to-base/`);
  },

  /** Broker admin submits a custom domain -> PENDING + verification token. */
  async submitDomain(domain: string): Promise<DomainSubmitResponse> {
    const res = await api.post<DomainSubmitResponse>(`${BASE}/domains/submit/`, { domain });
    return res.data;
  },

  /** Country admin approves a pending domain (keyed by domain string, not id). */
  async approveDomain(domain: string): Promise<DomainApproveResponse> {
    const res = await api.post<DomainApproveResponse>(`${BASE}/domains/approve/`, { domain });
    return res.data;
  },

  /** The broker's own public-site status (enabled flag, slug, live URL). */
  async getPublicSiteStatus(): Promise<PublicSiteStatus> {
    const res = await api.get<PublicSiteStatus>(`${BASE}/public-site/`);
    return res.data;
  },

  /** Broker-admin self-service: turn the public site on/off, set slug/description. */
  async updatePublicSiteStatus(payload: PublicSiteUpdate): Promise<PublicSiteStatus> {
    const res = await api.patch<PublicSiteStatus>(`${BASE}/public-site/`, payload);
    return res.data;
  },

  /**
   * Resolve the current broker's `public_profile_slug` for the temp preview link.
   *
   * There is no authenticated "my public slug" endpoint, so we read the public
   * directory (AllowAny, under /policy/api/public/ — NOT the /api/v1 base) and
   * match by broker id. Returns null when the broker hasn't enabled its public
   * profile (it won't appear in the directory, and there is no live site to
   * preview anyway).
   */
  async getPublicSlug(brokerId: string): Promise<string | null> {
    const res = await axios.get('/policy/api/public/brokers/');
    const rows: Array<{ id: string; public_profile_slug: string }> =
      res.data?.results ?? res.data ?? [];
    const match = rows.find((b) => b.id === brokerId);
    return match?.public_profile_slug ?? null;
  },

  /** The broker's catalog of insurers + products with per-item public visibility. */
  async getCatalog(): Promise<MicrositeCatalog> {
    const res = await api.get<MicrositeCatalog>(`${BASE}/catalog/`);
    return res.data;
  },

  /** Show/hide one insurer or product page on the public site. */
  async setItemVisibility(
    itemType: MicrositeVisibilityItemType,
    itemId: string,
    visible: boolean,
  ): Promise<void> {
    await api.post(`${BASE}/visibility/`, {
      item_type: itemType,
      item_id: itemId,
      visible,
    });
  },

  /** Master switch: clear the whole hide-list so every page is public again. */
  async makeAllPublic(): Promise<void> {
    await api.post(`${BASE}/visibility/`, { all_public: true });
  },

  // ─── Theme ───────────────────────────────────────────────────────────────

  /** The effective theme for the caller's scope, plus the field vocabulary. */
  async getTheme(): Promise<MicrositeThemeResponse> {
    const res = await api.get<MicrositeThemeResponse>(`${BASE}/theme/`);
    return res.data;
  },

  /** Save the theme for the caller's scope (creates the row if absent). */
  async saveTheme(tokens: ThemeTokens): Promise<MicrositeThemeResponse> {
    const res = await api.put<MicrositeThemeResponse>(`${BASE}/theme/`, { tokens });
    return res.data;
  },

  /** Drop the broker's own theme row so the platform default is inherited again. */
  async resetTheme(): Promise<void> {
    await api.delete(`${BASE}/theme/`);
  },

  // ─── Reusable section templates ──────────────────────────────────────────

  /** The caller's saved sections plus the shared platform ones. */
  async listSectionTemplates(): Promise<MicrositeSectionTemplate[]> {
    const res = await api.get<MicrositeSectionTemplate[] | { results: MicrositeSectionTemplate[] }>(
      `${BASE}/section-templates/`,
    );
    const data = res.data;
    return Array.isArray(data) ? data : (data?.results ?? []);
  },

  /** Save a configured section for reuse on another page. */
  async createSectionTemplate(payload: {
    name: string;
    section_type: string;
    config: Record<string, unknown>;
  }): Promise<MicrositeSectionTemplate> {
    const res = await api.post<MicrositeSectionTemplate>(`${BASE}/section-templates/`, payload);
    return res.data;
  },

  async deleteSectionTemplate(id: string): Promise<void> {
    await api.delete(`${BASE}/section-templates/${id}/`);
  },

  // ─── Live preview ────────────────────────────────────────────────────────

  /**
   * Render a draft layout with the real public templates and return the HTML.
   *
   * Nothing is saved. The server does the rendering on purpose: a preview built
   * from a second, React implementation of the templates is a preview that can
   * disagree with the live site, which is worse than no preview at all.
   */
  async renderPreview({
    pageType,
    sections,
    device,
    theme,
  }: MicrositePreviewRequest): Promise<MicrositePreviewResponse> {
    const res = await api.post<MicrositePreviewResponse>(`${BASE}/preview/`, {
      page_type: pageType,
      sections,
      device,
      // `undefined` means "use the saved theme"; a draft object overrides it.
      ...(theme ? { theme } : {}),
    });
    return res.data;
  },
};
